import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ListingCard = ({ listing }) => {
  const [isSaved, setIsSaved] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedListings = JSON.parse(localStorage.getItem('savedListings') || '[]');
    setIsSaved(savedListings?.includes(listing?.id));
  }, [listing?.id]);

  const handleSave = (e) => {
    e?.stopPropagation();

    const isAuthenticated = localStorage.getItem('authToken');
    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }

    const savedListings = JSON.parse(localStorage.getItem('savedListings') || '[]');
    let updatedSaved;

    if (isSaved) {
      updatedSaved = savedListings?.filter(id => id !== listing?.id);
    } else {
      updatedSaved = [...savedListings, listing?.id];
    }

    localStorage.setItem('savedListings', JSON.stringify(updatedSaved));
    setIsSaved(!isSaved);
  };

  const handleCardClick = () => {
    navigate('/business-detail', { state: { listing } });
  };

  const handleAuthPromptClose = () => {
    setShowAuthPrompt(false);
  };

  const handleAuthRedirect = () => {
    localStorage.setItem('pendingAction', JSON.stringify({
      type: 'save',
      listingId: listing?.id
    }));
    navigate('/login');
  };

  const getCategoryColor = (category) => {
    const colors = {
      'mindfulness-meditation': 'bg-purple-100 text-purple-800',
      'yoga-movement': 'bg-green-100 text-green-800',
      'energy-healing': 'bg-blue-100 text-blue-800',
      'complementary-therapies': 'bg-orange-100 text-orange-800',
      'nutrition-wellness': 'bg-emerald-100 text-emerald-800',
      'spirituality-esoterica': 'bg-indigo-100 text-indigo-800',
      'sound-breathwork': 'bg-cyan-100 text-cyan-800',
      'coaching-development': 'bg-yellow-100 text-yellow-800',
      'creative-therapy': 'bg-pink-100 text-pink-800',
      'retreats-immersions': 'bg-teal-100 text-teal-800',
      'nature-eco-therapy': 'bg-lime-100 text-lime-800',
      'ceremonies-community': 'bg-violet-100 text-violet-800',
      'training-professional': 'bg-slate-100 text-slate-800',
      'courses': 'bg-amber-100 text-amber-800',
      'circles': 'bg-rose-100 text-rose-800'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-800';
  };

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance?.toFixed(1)}km`;
  };

  return (
    <>
      <div
        className="bg-card rounded-lg shadow-organic hover:shadow-organic-md transition-organic cursor-pointer group overflow-hidden"
        onClick={handleCardClick}
      >
        {/* Image Container */}
        <div className="relative h-48 overflow-hidden">
          <Image
            src={listing?.image}
            alt={listing?.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-organic"
          />

          {/* Save Button */}
          <button
            onClick={handleSave}
            className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-organic ${
              isSaved
                ? 'bg-red-500 text-white' :'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
            }`}
            aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
          >
            <Icon
              name="Heart"
              size={18}
              className={isSaved ? 'fill-current' : ''}
            />
          </button>

          {/* Featured Badge */}
          {listing?.featured && (
            <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
              Featured
            </div>
          )}

          {/* Online Badge */}
          {listing?.isOnline && (
            <div className="absolute bottom-3 left-3 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
              <Icon name="Video" size={12} />
              <span>Online</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Business Info */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-foreground text-lg mb-1 line-clamp-1">
                {listing?.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                by {listing?.practitioner}
              </p>
            </div>
            {listing?.businessLogo && (
              <div className="w-10 h-10 rounded-full overflow-hidden ml-3 flex-shrink-0">
                <Image
                  src={listing?.businessLogo}
                  alt={`${listing?.practitioner} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-foreground mb-3 line-clamp-2">
            {listing?.description}
          </p>

          {/* Location and Distance */}
          <div className="flex items-center text-sm text-muted-foreground mb-3">
            <Icon name="MapPin" size={14} className="mr-1" />
            <span className="mr-2">{listing?.location}</span>
            {listing?.distance && (
              <>
                <span className="mx-1">•</span>
                <span>{formatDistance(listing?.distance)} away</span>
              </>
            )}
          </div>

          {/* Category and Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(listing?.category)}`}>
              {listing?.categoryLabel}
            </span>
            {listing?.tags && listing?.tags?.slice(0, 2)?.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Price and Date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {listing?.nextDate && (
                <div className="text-sm text-muted-foreground">
                  {new Date(listing.nextDate)?.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short'
                  })}
                </div>
              )}
            </div>

            {/* Rating */}
            {listing?.rating && (
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={14} className="text-yellow-500 fill-current" />
                <span className="text-sm font-medium text-foreground">
                  {listing?.rating}
                </span>
                <span className="text-sm text-muted-foreground">
                  ({listing?.reviewCount})
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Authentication Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleAuthPromptClose}
          />
          <div className="relative w-full max-w-md bg-popover rounded-lg shadow-organic-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Save This Listing
                </h3>
                <button
                  onClick={handleAuthPromptClose}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-6">
                Sign in to save listings and access them later from your profile.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleAuthPromptClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleAuthRedirect}
                  className="flex-1"
                >
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListingCard;
